// Deps
import { FelaComponent, createComponent } from "react-fela";
import { textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";

// Components
import Visual from "~/components/shared/visual";
import { ContentMaxWidth } from "~/components/layout/max-width";
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";

export default function CardGrid({ block }) {
    const { heading, copy, values, anchor_id, alignment, columns } = block;

    const theValue = (value, collectionLength, index) => {

        const { link_content, link_type, cta_link } = value;

        const link = getLinkFromRelationship(link_type, link_content, cta_link);

        return (
            <ValueContent
                collectionLength={collectionLength}
                key={index}
                alignment={alignment}
                columns={columns}
                className="grid-card"
            >
                <HeadingContainer>
                    {value.image && (
                        <IconContainer>
                            {value.image && (
                                <Visual
                                    extend={imageStyles(alignment)}
                                    image={value.image}
                                    background="contain"
                                />
                            )}
                        </IconContainer>
                    )}

                    {value.heading &&
                        <FelaComponent
                            as="h4"
                            style={combineRules(textStyles.heading4, valueHeading)}
                        >
                            <span dangerouslySetInnerHTML={{ __html: value.heading }} />
                        </FelaComponent>
                    }
                </HeadingContainer>
                <ValueDescription
                    extend={textStyles.paragraphSmall}
                    dangerouslySetInnerHTML={{
                        __html: value.description
                    }}
                />

                {link && value && link.url && value.cta_label && (
                    <WordpressLink
                        label={value.cta_label}
                        url={link.url}
                        slug={link.slug}
                        layout="text"
                        extend={{
                            fontSize: 20,
                            display: "inline-block",
                            extend: [fluid("margin-top", { max: 30, min: 20 })]
                        }}
                    />
                )}
            </ValueContent>
        );
    };

    return (
        <Container id={anchor_id} className="module-value-grid">
            <ContentMaxWidth
                extend={contentWidth}
            >
                {/* Heading */}
                {heading && (
                    <FelaComponent
                        as="h2"
                        style={combineRules(
                            textStyles.heading2,
                            componentHeading
                        )}
                    >
                        <span dangerouslySetInnerHTML={{ __html: heading }} />
                    </FelaComponent>
                )}

                {/* Copy */}
                {copy && (
                    <FelaComponent
                        as="p"
                        style={combineRules(
                            textStyles.paragraphXL,
                            componentCopy
                        )}
                    >
                        <span dangerouslySetInnerHTML={{ __html: heading }} />
                    </FelaComponent>
                )}

                {values && values.length && (
                    <ValueGridInner alignment={alignment}>
                        {values.map((value, index) => {
                            return theValue(value, values.length, index);
                        })}
                    </ValueGridInner>
                )}
            </ContentMaxWidth>
        </Container>
    );
}

const Container = createComponent(
    () => ({
        overflow: "hidden",

        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["id"]
);

const HeadingContainer = createComponent(
    () => ({
          display: "flex",
          flexDirection: "column",
          extend: [
              fluid("margin-bottom", { max: 20, min: 10 }),
          ]
    }),
    "div",
    ["id"]
);
const IconContainer = createComponent(() => ({
    position: "relative",
    marginBottom: 12,
}));

const contentWidth = {
    maxWidth: 1200
}

const componentHeading = {
    textAlign: "center",
    marginLeft: "auto",
    marginRight: "auto",
    extend: [fluid("marginBottom", { min: 10, max: 20 })],

    maxWidthTablet: {
        marginH: "auto",
        paddingH: 20
    }
};

const componentCopy = {
    textAlign: "center",
    marginLeft: "auto",
    marginRight: "auto",
    extend: [fluid("marginBottom", { min: 40, max: 80 })],

    maxWidthTablet: {
        marginH: "auto",
        paddingH: 20
    }
};

const valueHeading = {
    fontWeight: "bold",
    "& span a": {
        color: "inherit"
    }
};

const ValueGridInner = createComponent(({ alignment }) => ({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "left",

    extend: [
        {
            condition: alignment === "center",
            style: {
                justifyContent: "center"
            }
        }
    ]
}));

const imageStyles = alignment => ({
    "& .vv-visual": {
        width: "100%",
        height: "100%"
    },

    width: 36,
    height: 36,

    maxWidthMobile: {
        marginH: "auto",
        width: 24,
        height: 24
    },

    extend: [
        {
            condition: alignment === "center",
            style: {
                marginH: "auto"
            }
        }
    ]
});

const ValueContent = createComponent(
    ({ collectionLength, alignment, columns }) => ({
        borderRadius: "12px",
        backgroundColor: "#fff",
        filter: "drop-shadow(6px 3px 9px #ccc)",
        padding: 24,
        transition: "transform 0.3s ease",

        "&:hover": {
            transform: "translateY(-12px)"
        },

        minWidthTabletLandscape: {
            grid: {
                cols: "3",
                margins: 40,
                bottomMargin: "60px !important",
                length: collectionLength
            },
            extend: [
                {
                    condition: columns === "two",
                    style: {
                        grid: {
                            cols: "2",
                            margins: 20,
                            bottomMargin: 60,
                            length: collectionLength
                        }
                    }
                },
                {
                    condition: columns === "four",
                    style: {
                        grid: {
                            cols: "4",
                            margins: 40,
                            bottomMargin: 60,
                            length: collectionLength
                        }
                    }
                }
            ],
        },
        minWidthMobile: {
            maxWidthTabletLandscape: {
                grid: {
                    cols: "2",
                    margins: 40,
                    bottomMargin: 100 * 0.66,
                    length: collectionLength
                }
            }
        },
        maxWidthMobile: {
            marginBottom: 40,
            minWidth: "90%",
            maxWidth: "90%",
            marginH: "auto",

            // All columns are centered on mobile
            textAlign: "center"
        },
        extend: [
            {
                condition: alignment === "center",
                style: {
                    textAlign: "center"
                }
            }
        ]
    })
);

const ValueDescription = createComponent(() => ({}), "p", [
    "dangerouslySetInnerHTML"
]);
